/**
 * DESIGN TOKENS — Single source of truth for visual language.
 * Do NOT hardcode colors, spacing, radius or shadows in components.
 * Always consume via CSS variables (see theme/ThemeProvider.js) or `tokens.*`.
 * These defaults get overridden by BrandingProvider at runtime with values from /api/branding.
 */

export const defaultTokens = {
  // Colors — semantic, applied as CSS vars on :root
  color: {
    // Brand
    primary: "#0F4C81",
    primaryFg: "#FFFFFF",
    secondary: "#F2A65A",
    secondaryFg: "#1B1B1B",
    accent: "#1D6E5F",
    accentFg: "#FFFFFF",
    // Surfaces (never dark-on-dark)
    bg: "#F8F7F4",           // page bg — warm off-white
    surface: "#FFFFFF",
    surfaceMuted: "#F2EFE9",
    border: "#DDD6C7",
    // Text
    text: "#1B1B1B",
    textMuted: "#6B6558",
    textInverse: "#FFFFFF",
    // Status
    success: "#2F855A",
    warning: "#B7791F",
    danger: "#B03A2E",
    info: "#2C5282",
  },

  // Typography — deliberately editorial serif+mono pairing (not Inter/Roboto)
  font: {
    display: `"Fraunces", "Playfair Display", ui-serif, Georgia, serif`,
    body: `"IBM Plex Sans", "Segoe UI", system-ui, -apple-system, sans-serif`,
    mono: `"JetBrains Mono", "Fira Code", ui-monospace, monospace`,
    // Sizes
    xs: "0.75rem",
    sm: "0.875rem",
    base: "1rem",
    lg: "1.125rem",
    xl: "1.375rem",
    "2xl": "1.75rem",
    "3xl": "2.25rem",
    "4xl": "3rem",
    // Weights
    weightBody: 400,
    weightMedium: 500,
    weightBold: 700,
  },

  // Spacing scale (rem)
  space: {
    1: "0.25rem", 2: "0.5rem", 3: "0.75rem", 4: "1rem",
    5: "1.25rem", 6: "1.5rem", 8: "2rem", 10: "2.5rem",
    12: "3rem", 16: "4rem", 20: "5rem",
  },

  // Radius
  radius: {
    none: "0",
    sm: "0.375rem",
    md: "0.75rem",
    lg: "1rem",
    xl: "1.5rem",
    pill: "999px",
  },

  // Shadows
  shadow: {
    none: "none",
    sm: "0 1px 2px rgba(15, 23, 42, 0.06)",
    md: "0 4px 12px rgba(15, 23, 42, 0.08)",
    lg: "0 10px 30px rgba(15, 23, 42, 0.10)",
    ring: "0 0 0 3px rgba(15, 76, 129, 0.25)",
  },

  // Motion
  motion: {
    fast: "120ms cubic-bezier(0.2, 0.6, 0.2, 1)",
    base: "220ms cubic-bezier(0.2, 0.6, 0.2, 1)",
    slow: "420ms cubic-bezier(0.2, 0.6, 0.2, 1)",
  },
};

/** Maps token keys to CSS variable names — used by ThemeProvider. */
export const CSS_VAR_MAP = {
  "color.primary": "--ds-color-primary",
  "color.primaryFg": "--ds-color-primary-fg",
  "color.secondary": "--ds-color-secondary",
  "color.secondaryFg": "--ds-color-secondary-fg",
  "color.accent": "--ds-color-accent",
  "color.accentFg": "--ds-color-accent-fg",
  "color.bg": "--ds-color-bg",
  "color.surface": "--ds-color-surface",
  "color.surfaceMuted": "--ds-color-surface-muted",
  "color.border": "--ds-color-border",
  "color.text": "--ds-color-text",
  "color.textMuted": "--ds-color-text-muted",
  "color.success": "--ds-color-success",
  "color.warning": "--ds-color-warning",
  "color.danger": "--ds-color-danger",
  "color.info": "--ds-color-info",
  "font.display": "--ds-font-display",
  "font.body": "--ds-font-body",
  "font.mono": "--ds-font-mono",
  "radius.sm": "--ds-radius-sm",
  "radius.md": "--ds-radius-md",
  "radius.lg": "--ds-radius-lg",
  "radius.xl": "--ds-radius-xl",
  "radius.pill": "--ds-radius-pill",
  "shadow.sm": "--ds-shadow-sm",
  "shadow.md": "--ds-shadow-md",
  "shadow.lg": "--ds-shadow-lg",
  "motion.fast": "--ds-motion-fast",
  "motion.base": "--ds-motion-base",
  "motion.slow": "--ds-motion-slow",
};
